import {
  ApprovalDecision,
  ApprovalDecisionKind,
  ApprovalDetail,
  ApprovalPayload,
  ApprovalRequest,
  ApprovalStatus,
} from 'models/approvals/approval';
import { CapabilityType } from 'models/capabilities/capability';
import {
  ApprovalDecisionDTO,
  ApprovalDetailDTO,
  ApprovalPayloadDTO,
  ApprovalRequestDTO,
  DecideApprovalDTO,
} from 'dto/approvals/approvalDTO';
import { ToolCallAccess, ToolCallIdentityMode } from 'models/executions/execution';
import { ApproverMode } from 'models/policies/policy';
import { fromApiDateTimeOptional, fromApiDateTimeRequired } from 'utils/datetime';

function mapFromApprovalDecision(dto: ApprovalDecisionDTO): ApprovalDecision {
  const output = new ApprovalDecision();
  output.approvalDecisionId = dto.approvalDecisionId;
  output.deciderUserId = dto.deciderUserId;
  output.deciderName = dto.deciderName ?? '';
  output.isSystem = dto.isSystem;
  output.decision = dto.decision as ApprovalDecisionKind;
  output.comment = dto.comment ?? undefined;
  output.createdAt = fromApiDateTimeRequired(dto.createdAt);

  return output;
}

function fillApprovalRequest(output: ApprovalRequest, dto: ApprovalRequestDTO): void {
  output.approvalRequestId = dto.approvalRequestId;
  output.executionId = dto.executionId;
  output.toolCallId = dto.toolCallId;
  output.skillId = dto.skillId ?? undefined;
  output.skillVersionId = dto.skillVersionId ?? undefined;
  output.skillName = dto.skillName ?? undefined;
  output.skillVersion = dto.skillVersion ?? undefined;
  output.requesterUserId = dto.requesterUserId;
  output.requesterName = dto.requesterName ?? '';
  output.reason = dto.reason ?? '';
  output.approverMode = dto.approverMode as ApproverMode;
  output.approverGroupId = dto.approverGroupId ?? undefined;
  output.approverGroupName = dto.approverGroupName ?? undefined;
  output.status = dto.status as ApprovalStatus;
  output.createdAt = fromApiDateTimeRequired(dto.createdAt);
  output.expiresAt = fromApiDateTimeRequired(dto.expiresAt);
  output.decidedAt = fromApiDateTimeOptional(dto.decidedAt ?? undefined);
  output.toolName = dto.toolName ?? '';
  output.operation = dto.operation ?? undefined;
  output.capabilityName = dto.capabilityName ?? undefined;
  output.canDecide = dto.canDecide;
  output.decisions = (dto.decisions ?? []).map(mapFromApprovalDecision);
}

export function mapFromApprovalRequest(dto: ApprovalRequestDTO): ApprovalRequest {
  const output = new ApprovalRequest();
  fillApprovalRequest(output, dto);

  return output;
}

function mapFromApprovalPayload(dto: ApprovalPayloadDTO | undefined): ApprovalPayload {
  const output = new ApprovalPayload();
  if (!dto) return output;

  output.action = dto.action ?? '';
  output.downstreamSystem = dto.downstreamSystem ?? '';
  output.toolName = dto.toolName ?? '';
  output.operation = dto.operation ?? undefined;
  output.access = dto.access as ToolCallAccess;
  output.arguments = dto.arguments ?? undefined;
  output.argumentsTruncated = dto.argumentsTruncated;
  output.capabilityId = dto.capabilityId ?? undefined;
  output.capabilityKey = dto.capabilityKey ?? undefined;
  output.capabilityName = dto.capabilityName ?? undefined;
  output.capabilityType = dto.capabilityType as CapabilityType;
  output.identityMode = dto.identityMode as ToolCallIdentityMode;
  output.skillId = dto.skillId ?? undefined;
  output.skillKey = dto.skillKey ?? undefined;
  output.skillName = dto.skillName ?? undefined;
  output.skillVersionId = dto.skillVersionId ?? undefined;
  output.skillVersion = dto.skillVersion ?? undefined;
  output.requesterUserId = dto.requesterUserId ?? '';
  output.requesterName = dto.requesterName ?? '';
  output.ruleId = dto.ruleId ?? undefined;
  output.policyId = dto.policyId ?? undefined;
  output.approverCount = dto.approverCount ?? 0;

  return output;
}

export function mapFromApprovalDetail(dto: ApprovalDetailDTO): ApprovalDetail {
  const output = new ApprovalDetail();
  fillApprovalRequest(output, dto);
  output.payload = mapFromApprovalPayload(dto.payload);
  output.relatedApprovals = (dto.relatedApprovals ?? []).map(mapFromApprovalRequest);

  return output;
}

//an empty comment is sent as none
export function mapToDecideApproval(comment: string | undefined): DecideApprovalDTO {
  const dto = new DecideApprovalDTO();
  const text = comment?.trim();
  dto.comment = text ? text : undefined;

  return dto;
}
